import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Receipt, ShieldCheck, BarChart3, Users, Check, Sparkles } from "lucide-react";
import Logo from "@/components/Logo";
import { LANDING } from "@/constants/testIds";

const HERO_IMG = "https://images.unsplash.com/photo-1593113616828-6f22bca04804?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxjb21tdW5pdHklMjB2b2x1bnRlZXJzJTIwaGFuZHN8ZW58MHx8fHwxNzgxNTY4NDc4fDA&ixlib=rb-4.1.0&q=85";
const TRANSPARENCY_IMG = "https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxub25wcm9maXQlMjBkaXZlcnNlJTIwbWVldGluZ3xlbnwwfHx8fDE3ODE1Njg0Nzh8MA&ixlib=rb-4.1.0&q=85";
const FEATURE_IMG = "https://images.unsplash.com/photo-1554224154-26032ffc0d07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxmaW5hbmNpYWwlMjBhY2NvdW50aW5nJTIwZG9jdW1lbnR8ZW58MHx8fHwxNzgxNTY4NDc4fDA&ixlib=rb-4.1.0&q=85";

const PLANS = [
  {
    id: "community",
    name: "Community",
    price: 0,
    cadence: "Free forever",
    tagline: "For grassroots and volunteer-run groups under $50K/yr.",
    features: ["Up to 3 users", "50 receipts/month", "Mobile receipt capture", "Single approver workflow"],
    accent: false,
  },
  {
    id: "growth",
    name: "Growth",
    price: 29,
    cadence: "per month",
    tagline: "Most popular for small to mid-sized nonprofits.",
    features: ["Unlimited users", "Unlimited receipts", "AI receipt OCR (Gemini)", "Program budgets & alerts", "Board dashboard"],
    accent: true,
  },
  {
    id: "impact",
    name: "Impact",
    price: 149,
    cadence: "per month",
    tagline: "For mature mission-driven organizations.",
    features: ["Everything in Growth", "Grant reporting", "Donor transparency pages", "Multi-program rollups", "Priority support"],
    accent: false,
  },
];

export default function Landing() {
  return (
    <div className="bg-background text-foreground">
      {/* Sticky nav */}
      <header className="sticky top-0 z-30 bg-background/90 backdrop-blur border-b border-border/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-24 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <Logo height={56} />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#problem" className="under-link hover:text-foreground">Why TrustDime</a>
            <a href="#features" className="under-link hover:text-foreground">Features</a>
            <a href="#pricing" data-testid={LANDING.ctaPricing} className="under-link hover:text-foreground">Pricing</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid={LANDING.navLogin} className="text-sm text-muted-foreground hover:text-foreground hidden md:inline">Sign in</Link>
            <Link
              to="/login?mode=signup"
              data-testid={LANDING.navSignup}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Get started <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
              <Sparkles size={12} className="text-secondary" />
              Financial accountability for mission-driven teams
            </div>
            <h1 className="serif mt-6 text-5xl md:text-6xl lg:text-7xl leading-[1.02] font-light text-primary tracking-tight">
              Every <em className="italic font-medium" style={{ color: "hsl(45 85% 40%)" }}>dime</em> tells the story of trust.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
              TrustDime turns volunteer reimbursements, program budgets, and donor reporting
              into a single transparent ledger — built for nonprofits, religious organizations,
              and community groups that move on faith, not spreadsheets.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/login?mode=signup"
                data-testid={LANDING.ctaGetStarted}
                className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Start free — Community plan
                <ArrowUpRight size={16} />
              </Link>
              <a href="#pricing" className="text-sm text-foreground under-link">See pricing →</a>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-8 max-w-lg">
              {[
                { k: "Trust", v: "Receipts in seconds" },
                { k: "Clarity", v: "Live budget pulse" },
                { k: "Stewardship", v: "Donor transparency" },
              ].map((s) => (
                <div key={s.k}>
                  <div className="serif text-xl text-primary">{s.k}</div>
                  <div className="text-xs text-muted-foreground mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative grain-overlay rounded-md overflow-hidden border border-border shadow-[0_30px_60px_-30px_rgba(20,30,60,0.35)]">
              <img src={HERO_IMG} alt="Community volunteer hands" className="w-full h-[520px] object-cover" />
              <div className="absolute inset-x-4 bottom-4 bg-card/95 backdrop-blur border border-border rounded-sm p-4">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Live program</div>
                <div className="mt-1 flex items-baseline justify-between">
                  <div className="serif text-lg">Food Pantry · March</div>
                  <div className="mono text-sm text-primary">$2,840 <span className="text-muted-foreground">/ $4,000</span></div>
                </div>
                <div className="h-1.5 mt-3 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: "71%" }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM / SOLUTION */}
      <section id="problem" className="border-t border-border bg-card/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">The problem</div>
            <h2 className="serif text-4xl mt-3 leading-tight text-primary">QuickBooks isn't the answer. Trust is.</h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-muted-foreground leading-relaxed">
            <p>
              Volunteers send receipts on WhatsApp. Treasurers chase reimbursements through
              Venmo. Board members ask "where did the donation go?" — and nobody has a clean
              answer. Accounting software wasn't built for this.
            </p>
            <p>
              <span className="text-foreground font-medium">TrustDime is not an accounting system.</span> It's the
              accountability layer that lives between your team and your books — so every dollar
              has a name, an approval, and a story.
            </p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">What you get</div>
              <h2 className="serif text-4xl mt-3 leading-tight text-primary">Built around the four people who move money in your org.</h2>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {[
              {
                icon: Receipt,
                title: "Volunteers",
                kicker: "Snap. Tag. Sent.",
                body: "Take a photo of any receipt; Gemini Vision OCR pulls vendor, total, and date in under 5 seconds. Tag the program, add a note, submit.",
                img: FEATURE_IMG,
              },
              {
                icon: ShieldCheck,
                title: "Treasurers",
                kicker: "Approve with confidence.",
                body: "A single inbox of pending expenses with receipt previews, program context, and one-click approve / reject / request info — with full audit trail.",
                img: TRANSPARENCY_IMG,
              },
              {
                icon: BarChart3,
                title: "Board members",
                kicker: "See the whole picture.",
                body: "Budget vs actual by program, monthly spend trends, and reimbursement pipeline — no spreadsheets, no quarterly scramble.",
                img: HERO_IMG,
              },
            ].map((f) => (
              <div key={f.title} className="border border-border bg-card rounded-md overflow-hidden flex flex-col">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={f.img} alt={f.title} className="w-full h-full object-cover grayscale-[40%] hover:grayscale-0 transition-all duration-700" />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                    <f.icon size={14} className="text-secondary" /> {f.title}
                  </div>
                  <div className="serif text-2xl mt-3 text-primary">{f.kicker}</div>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DONOR TRANSPARENCY STRIP */}
      <section className="border-y border-border bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.2em] text-secondary">Donor transparency</div>
            <h2 className="serif text-4xl mt-3 leading-tight">A public page that turns donors into stewards.</h2>
            <p className="mt-5 text-primary-foreground/80 max-w-xl leading-relaxed">
              Every Impact-tier org gets a shareable transparency page that maps program
              budgets to actual spending — so when a donor asks "what did my $50 do?",
              the answer is one link.
            </p>
          </div>
          <div className="lg:col-span-5 flex justify-end">
            <Users size={120} strokeWidth={0.75} className="text-secondary opacity-70" />
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Pricing</div>
              <h2 className="serif text-4xl mt-3 leading-tight text-primary">Honest pricing for honest work.</h2>
              <p className="mt-4 text-muted-foreground">Start free. Upgrade when accountability becomes a competitive advantage.</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {PLANS.map((p) => (
              <div
                key={p.id}
                data-testid={LANDING.planCard(p.id)}
                className={`relative border rounded-md p-7 flex flex-col bg-card ${
                  p.accent ? "border-secondary shadow-[0_20px_50px_-30px_rgba(212,170,40,0.6)]" : "border-border"
                }`}
              >
                {p.accent && (
                  <div className="absolute top-0 right-7 -translate-y-1/2 bg-secondary text-secondary-foreground text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-medium">
                    Most popular
                  </div>
                )}
                <div className="serif text-2xl text-primary">{p.name}</div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="serif text-5xl text-primary">{p.price === 0 ? "$0" : `$${p.price}`}</span>
                  <span className="text-sm text-muted-foreground">/ {p.cadence}</span>
                </div>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{p.tagline}</p>
                <ul className="mt-6 space-y-2.5 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={15} className="mt-0.5 text-secondary shrink-0" strokeWidth={2} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-7">
                  <Link
                    to={p.id === "community" ? "/login?mode=signup" : `/login?mode=signup&plan=${p.id}`}
                    data-testid={LANDING.planCta(p.id)}
                    className={`block text-center rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                      p.accent
                        ? "bg-secondary text-secondary-foreground hover:bg-secondary/90"
                        : "bg-primary text-primary-foreground hover:bg-primary/90"
                    }`}
                  >
                    {p.id === "community" ? "Get started free" : `Choose ${p.name}`}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border bg-card/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <Logo height={36} />
            <p className="text-xs text-muted-foreground mt-3">© {new Date().getFullYear()} TrustDime. Because every contribution, every volunteer, and every act of service counts.</p>
          </div>
          <div className="text-xs text-muted-foreground text-right">
            Built for nonprofits, religious organizations, and community groups.
            <div className="mt-1 flex gap-4 justify-end">
              <a href="#features" className="under-link hover:text-foreground">Features</a>
              <a href="#pricing" className="under-link hover:text-foreground">Pricing</a>
              <a href="mailto:hello@trustdime.app" className="under-link hover:text-foreground">Contact</a>
            </div>
            <div className="mt-3 text-[11px]">
              Powered By <a href="https://www.itprofound.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">ITProFound</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
